import { useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Icon from '../components/ui/Icon.jsx';
import ProductCard from '../components/ui/ProductCard.jsx';
import SectionHeader from '../components/ui/SectionHeader.jsx';
import RichTextViewer from '../components/ui/RichTextViewer.jsx';
import ProductImage from '../components/ui/ProductImage.jsx';
import { useApi } from '../hooks/useApi.js';
import { getProduct, getRelatedProducts } from '../api/productsApi.js';
import { useCart } from '../context/CartContext.jsx';
import { useToast } from '../context/ToastContext.jsx';
import { analyticsEvent, gaItem } from '../utils/analytics.js';
import './ProductDetailPage.css';

function ProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, items: cartItems } = useCart();
  const { showToast } = useToast();

  const { data: product, loading } = useApi(() => getProduct(id), [id]);
  const { data: related = [] }     = useApi(() => getRelatedProducts(id, 4), [id]);

  const [qty, setQty] = useState(1);               // 數字；輸入過程允許暫時為 ''
  const [selected, setSelected] = useState({});   // { [選項類型]: 值 }
  const [activeImg, setActiveImg] = useState(0);   // 目前主圖在圖庫中的 index

  // 此商品的選項類型（後台定義的維度，如 ["重量","鏢尖"]）與所有 variant
  const optionTypes = useMemo(() => product?.optionTypes || [], [product]);
  const variants = useMemo(() => product?.variants || [], [product]);

  // 每個選項類型有哪些不重複的值（依出現順序）
  const valuesByType = useMemo(() => {
    const map = {};
    optionTypes.forEach(t => {
      const seen = [];
      variants.forEach(v => {
        const val = v.attrs?.[t];
        if (val != null && val !== '' && !seen.includes(val)) seen.push(val);
      });
      map[t] = seen;
    });
    return map;
  }, [optionTypes, variants]);

  // 組圖庫：商品主圖（共用）+ 各 variant 專屬圖，每張記住屬於哪個 variant（共用圖 variantId=null）
  // 去重：同一張 URL 只列一次（以先出現者為準）
  const gallery = useMemo(() => {
    const list = [];
    const seen = new Set();
    (product?.images || []).forEach(src => {
      if (src && !seen.has(src)) { seen.add(src); list.push({ src, variantId: null }); }
    });
    variants.forEach(v => {
      (v.images || []).forEach(src => {
        if (src && !seen.has(src)) { seen.add(src); list.push({ src, variantId: v.id }); }
      });
    });
    if (list.length === 0) list.push({ src: product?.images?.[0] || 'dart-full', variantId: null });
    return list;
  }, [product, variants]);

  // 商品載入 / 切換商品後，預設選第一個可售規格的屬性、主圖重置到第一張
  useEffect(() => {
    if (!product) return;
    const first = variants.find(v => v.active) || variants[0];
    const init = {};
    optionTypes.forEach(t => { init[t] = first?.attrs?.[t]; });
    setSelected(init);
    setActiveImg(0);
  }, [product, optionTypes, variants]);

  // 依目前選的值找出對應 variant；沒有選項時用第一個
  const selectedVariant = useMemo(() => variants.find(v =>
    optionTypes.length === 0 ? true : optionTypes.every(t => v.attrs?.[t] === selected[t])
  ) || variants[0] || null, [variants, optionTypes, selected]);

  useEffect(() => {
    if (!product || !selectedVariant) return;
    const viewed = {
      id: selectedVariant.id,
      name: product.name,
      price: selectedVariant.price ?? product.priceMin ?? product.price,
      categoryId: product.categoryId,
      tags: optionTypes.map(t => selectedVariant.attrs?.[t]).filter(Boolean),
    };
    analyticsEvent('view_item', {
      currency: 'TWD', value: viewed.price, items: [gaItem(viewed, 0)],
    });
  }, [product, selectedVariant, optionTypes]);

  // 庫存：API 規格有 stock 數字才檢查（舊 mock 沒有 → 不限制）
  const stock = typeof selectedVariant?.stock === 'number' ? selectedVariant.stock : null;
  const maxQty = stock === null ? 99 : Math.max(stock, 1);
  const soldOut = stock === 0;

  // 切換規格後數量不可超過該規格庫存
  useEffect(() => {
    setQty(q => Math.min(typeof q === 'number' ? q : 1, maxQty));
  }, [maxQty]);

  if (loading) {
    return <main className="pdp"><div style={{ padding: 80, textAlign: 'center', color: 'var(--ink-soft)' }}>載入中…</div></main>;
  }
  if (!product) {
    return (
      <main className="pdp">
        <p style={{ padding: 80, textAlign: 'center' }}>
          找不到商品。<Link to="/shop">返回商品列表</Link>
        </p>
      </main>
    );
  }

  // 顯示價格：優先用選到的 variant 價；否則 priceMin / 舊 mock price
  const price = selectedVariant?.price ?? product.priceMin ?? product.price;
  // 原價：有選到規格時只看「該規格自己的」原價（沒填就是沒有，不可借用別的規格）；
  //       沒有 variant 資料時（舊 mock）才退回商品層
  const originalPrice = selectedVariant
    ? selectedVariant.originalPrice
    : product.originalPrice;

  // 切換到圖庫第 i 張：更新主圖；若該圖屬於某規格則選項跳到該規格（不再反向改主圖，避免打架）
  const goToImage = (i) => {
    const len = gallery.length;
    if (len === 0) return;
    const idx = ((i % len) + len) % len;   // 環狀：超出頭尾自動繞回
    setActiveImg(idx);
    const g = gallery[idx];
    if (g.variantId != null) {
      const v = variants.find(vv => vv.id === g.variantId);
      if (v) setSelected(prev => {
        const next = { ...prev };
        optionTypes.forEach(t => { next[t] = v.attrs?.[t]; });
        return next;
      });
    }
  };

  // 點「選項按鈕」選規格：更新選項，並把主圖跳到「該規格的第一張圖」（若該規格有圖）
  const selectOption = (type, val) => {
    const next = { ...selected, [type]: val };
    setSelected(next);
    const v = variants.find(vv =>
      optionTypes.length === 0 ? true : optionTypes.every(t => vv.attrs?.[t] === next[t])
    );
    if (v) {
      const idx = gallery.findIndex(g => g.variantId === v.id);
      if (idx >= 0) setActiveImg(idx);
    }
  };

  const handleAddToCart = (buyNow = false) => {
    if (!selectedVariant) { showToast('此商品暫無可購買的規格', 'error'); return; }
    const n = Math.max(1, Number(qty) || 1);
    // 提早擋庫存：連購物車裡同規格已有的數量一起算，不要等到結帳才被後端打回
    if (stock !== null) {
      const inCart = cartItems.find(i => (i.lineId ?? i.id) === selectedVariant.id)?.qty || 0;
      if (stock === 0) { showToast('此規格已售完', 'error'); return; }
      if (inCart + n > stock) {
        showToast(`庫存不足：此規格剩 ${stock} 件${inCart > 0 ? `（購物車已有 ${inCart} 件）` : ''}`, 'error');
        return;
      }
    }
    const attrsLabel = optionTypes.map(t => selectedVariant.attrs?.[t]).filter(Boolean);
    const cartItem = {
      lineId: selectedVariant.id,        // 購物車以 variant 為單位（不同規格各自一行）
      productId: product.id,
      variantId: selectedVariant.id,
      sku: selectedVariant.sku,
      name: product.name,
      price: selectedVariant.price ?? price,
      image: product.images[0],
      tags: attrsLabel,
    };
    analyticsEvent('add_to_cart', {
      currency: 'TWD',
      value: cartItem.price * n,
      items: [gaItem({ ...cartItem, qty: n }, 0)],
      action_type: buyNow ? 'buy_now' : 'add_to_cart',
    });
    for (let i = 0; i < n; i++) addToCart(cartItem);
    showToast(`已加入購物車 ×${n}`, 'success');
    if (buyNow) navigate('/cart');
  };

  return (
    <main className="pdp">
      <p className="crumbs">
        首頁 / <Link to="/shop">商品一覽</Link> / <Link to={`/shop?category=${product.categoryId}`}>{product.categoryId.toUpperCase()}</Link> / <b>{product.name}</b>
      </p>

      <section className="pdp-grid">
        <div className="gallery">
          <div className="gallery-hero">
            {gallery.length > 1 && (
              <button className="gallery-nav prev" aria-label="上一張"
                onClick={() => goToImage(activeImg - 1)}>‹</button>
            )}
            <ProductImage src={(gallery[activeImg] || gallery[0]).src} alt={product.name} />
            {gallery.length > 1 && (
              <button className="gallery-nav next" aria-label="下一張"
                onClick={() => goToImage(activeImg + 1)}>›</button>
            )}
          </div>
          <div className="thumbs">
            {gallery.map((g, i) => (
              <button
                key={i}
                className={`thumb${i === activeImg ? ' active' : ''}`}
                onClick={() => goToImage(i)}
              >
                <ProductImage src={g.src} alt="" />
              </button>
            ))}
          </div>
        </div>

        <div className="info">
          <div className="tag-row">
            <span className="tag brand">{product.brand}</span>
            {product.tags.includes('new')  && <span className="tag new">2025 新品</span>}
            {product.tags.includes('best') && <span className="tag steel">熱銷</span>}
          </div>
          <h1>{product.name}</h1>
          <div className="info-sub">{product.description}</div>

          <div className="price-row">
            <div>
              <span className="price-now"><span className="nt">NT$</span>{price.toLocaleString()}</span>
              {originalPrice && (
                <span className="price-was">NT${originalPrice.toLocaleString()}</span>
              )}
            </div>
            {originalPrice && (
              <span className="price-save">會員省 {(originalPrice - price).toLocaleString()}</span>
            )}
          </div>

          {/* 照後台定義的選項類型通用顯示，選一組對應到一個規格 */}
          {optionTypes.map(type => (
            <div className="spec-row" key={type}>
              <div className="spec-label">{type}</div>
              <div className="swatches">
                {valuesByType[type].map(val => {
                  const isOn = selected[type] === val;
                  // 是否有可售 variant 含此值
                  const hasActive = variants.some(v => v.attrs?.[type] === val && v.active);
                  return (
                    <button
                      key={val}
                      type="button"
                      className={`swatch${isOn ? ' on' : ''}${!hasActive ? ' dis' : ''}`}
                      onClick={() => hasActive && selectOption(type, val)}
                      disabled={!hasActive}
                    >
                      <b>{val}</b>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="qty-buy">
            <div className="qty">
              <button onClick={() => setQty(q => Math.max(1, (Number(q) || 1) - 1))} aria-label="減少">−</button>
              <input
                value={qty}
                inputMode="numeric"
                aria-label="數量"
                onChange={e => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setQty(digits === '' ? '' : Math.min(Number(digits), maxQty));
                }}
                onBlur={() => setQty(q => (Number(q) >= 1 ? Number(q) : 1))}
              />
              <button onClick={() => setQty(q => Math.min(maxQty, (Number(q) || 0) + 1))} aria-label="增加">+</button>
            </div>
            {stock !== null && (
              <span style={{ font: '600 12px "Noto Sans TC", sans-serif', color: stock <= 5 ? 'var(--red)' : 'var(--ink-soft)', alignSelf: 'center', whiteSpace: 'nowrap' }}>
                {soldOut ? '已售完' : `庫存 ${stock} 件`}
              </span>
            )}
            <button className="buy-btn alt" disabled={soldOut} style={soldOut ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              onClick={() => handleAddToCart(false)}>{soldOut ? '已售完' : '加入購物車'}</button>
            <button className="buy-btn" disabled={soldOut} style={soldOut ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              onClick={() => handleAddToCart(true)}>
              立即購買 <Icon name="arrow" size={18} />
            </button>
          </div>

          {Object.keys(product.specs).length > 0 && (
            <div className="info-specs">
              <div className="info-specs-title">商品規格</div>
              <div className="spec-grid">
                {Object.entries(product.specs).map(([key, value]) => (
                  <dl key={key}><dt>{key}</dt><dd>{value}</dd></dl>
                ))}
              </div>
            </div>
          )}

          <SpecVisualizer specs={product.specs} />
        </div>
      </section>

      {/* 商品詳細說明（共用 + 當前選中規格各自的說明） */}
      {(product.detailHtml || selectedVariant?.detailHtml) && (
        <section className="tabs-wrap">
          <div className="tabs">
            <div className="tab on">商品說明</div>
          </div>
          {product.detailHtml && <RichTextViewer html={product.detailHtml} />}
          {selectedVariant?.detailHtml && (
            <div className="variant-detail">
              {optionTypes.length > 0 && (
                <h3 className="variant-detail-title">
                  {optionTypes.map(t => selectedVariant.attrs?.[t]).filter(Boolean).join(' · ')}
                </h3>
              )}
              <RichTextViewer html={selectedVariant.detailHtml} />
            </div>
          )}
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="section" style={{ padding: '64px 0 0' }}>
          <SectionHeader eyebrow="You May Also Like" title="相關商品" linkTo="/shop" linkText="更多商品" />
          <div className="product-grid">
            {related.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </main>
  );
}

function SpecVisualizer({ specs }) {
  if (!specs) return null;

  const tungsten = specs['鎢含量'] || specs['Tungsten'] || specs['tungsten'];
  const balance  = specs['重心'] || specs['Balance'] || specs['balance'];
  const grip     = specs['握感'] || specs['Grip'] || specs['grip'];

  const meters = [
    tungsten && {
      label: '鎢含量 Tungsten',
      value: tungsten,
      pct: parseInt(tungsten) || 90,
      scale: ['80%', '90%', '95%', '100%'],
    },
    balance && {
      label: '重心位置 Balance',
      value: balance,
      pct: balance.includes('前') ? 70 : balance.includes('後') ? 30 : 50,
      scale: ['FRONT', 'CENTER', 'REAR'],
    },
    grip && {
      label: '握感粒度 Grip',
      value: grip,
      pct: grip.includes('細') ? 30 : grip.includes('粗') ? 80 : 50,
      scale: ['SMOOTH', 'MEDIUM', 'AGGRESSIVE'],
    },
  ].filter(Boolean);

  if (meters.length === 0) return null;

  return (
    <div className="pdp-spec-viz">
      <h4>規格視覺化 · SPEC AT A GLANCE</h4>
      {meters.map(m => (
        <div key={m.label} className="meter">
          <div className="top"><b>{m.label}</b><small>{m.value}</small></div>
          <div className="bar"><i style={{ width: `${m.pct}%` }} /></div>
          <div className="scale">{m.scale.map(s => <span key={s}>{s}</span>)}</div>
        </div>
      ))}
    </div>
  );
}

export default ProductDetailPage;
